import { useQuery } from '@apollo/client';
import { GET_ME } from '../queries/getMe';

/**
 * Who gets the /leadership page.
 *
 * **Reads the server's own answer** (`Membership.canAccessLeadership`) rather than rebuilding it
 * from role names here. The server gate is the `AUTHOR_LEADERSHIP_CONTENT` capability, and which
 * roles carry it is per-organisation data that changes on the roles page — so any list of role
 * names kept on this side is a copy that silently goes stale.
 *
 * It already had: this used to OR `isPartnerLeadership` with `isPartnerMembershipOfficer`, which
 * hid the page from a supporter organisation's `PARTNER_ADMIN` while the server served it quite
 * happily.
 *
 * Still deliberately excludes a *community* org's admin — `ORG_ADMIN` does not carry the
 * capability, because administering an organisation is not speaking for it.
 */
export const useCanAccessLeadership = () => {
  const { data, loading, error } = useQuery(GET_ME);
  if (loading || error) return false;
  return data?.me?.organisation?.membership?.canAccessLeadership ?? false;
};

/** The working organisation's name, used to brand its announcements (e.g. "SWBC Updates"). */
export const useOrganisationName = (): string | null => {
  const { data, loading, error } = useQuery(GET_ME);
  if (loading || error) return null;
  return data?.me?.organisation?.name ?? null;
};
